/**
 * GeneratedDocumentsView — the production workspace's "Generated documents"
 * review home (ship-gate Step 2d).
 *
 * Lists the MODEL-DERIVED documentation that acceptance produces in the
 * production workspace (doc_kind === "model_derived" — the "Model
 * documentation" doc: a node-by-node walkthrough of the model's computational
 * logic) and lets a human read, edit, and approve each one. Rendering + editing
 * reuse the SAME report machinery as
 * research + documentation (reportMarkdown's shared surface + ReportEditor) so
 * the three surfaces can't drift.
 *
 * Two panes: a left list (title · provenance · status) + a right reader/editor.
 * Each doc shows typed provenance ("derived · model vN") and an approve action;
 * approval is hash-pinned by the backend and cleared whenever the model is
 * re-derived (so a model change resets it to "needs review").
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { useProjectStore } from '../../stores/projectStore';
import { approveDocFile, updateDocFileContent } from '../../apiService';
import {
  buildBaseMarkdownComponents,
  preprocessDocumentMarkdown,
} from '../workspace/reportMarkdown';
import ReportEditor from '../workspace/ReportEditor';
import ConfirmDialog from '../ui/ConfirmDialog';
import { formatProvenance } from './docProvenance';
import { formatDateTime } from '../../utils/formatDateTime';
import { FileText, CheckCircle2, Clock, Loader2, AlertCircle, Pencil, X, Check } from 'lucide-react';
import type { DictDocFile } from '../../types/workspaceState';

interface GeneratedDocumentsViewProps {
  workspaceId: string | null;
  authToken?: string;
  userId?: string;
}

/** Format an ISO timestamp safely — a malformed value renders nothing. */
function formatApprovedAt(value: string | null | undefined): string {
  if (!value) return '';
  const formatted = formatDateTime(value, { invalidFallback: '' });
  return formatted ? ` · ${formatted}` : '';
}

export default function GeneratedDocumentsView({
  workspaceId,
  authToken,
  userId,
}: GeneratedDocumentsViewProps) {
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);
  const loadSpecificWorkspace = useProjectStore((s) => s.loadSpecificWorkspace);

  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [busyFileId, setBusyFileId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inline edit (one doc at a time) — ReportEditor is uncontrolled via the ref.
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [savingFileId, setSavingFileId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const editorRef = useRef<{ getMarkdown: () => string } | null>(null);
  const editBaselineRef = useRef<string>('');
  const hasUnsavedRef = useRef<boolean>(false);

  // The model-derived docs this production workspace holds (Slice 2c produces
  // them on acceptance). Stable order by file_id so the list doesn't jump.
  const docs = useMemo<DictDocFile[]>(() => {
    const all = Object.values(dictWorkspace?.doc_files ?? {});
    return all
      .filter((f) => f.doc_kind === 'model_derived')
      .sort((a, b) => a.file_id.localeCompare(b.file_id));
  }, [dictWorkspace?.doc_files]);

  // Keep a valid selection as docs load / change.
  useEffect(() => {
    if (docs.length === 0) {
      if (selectedFileId !== null) setSelectedFileId(null);
      return;
    }
    if (!selectedFileId || !docs.some((d) => d.file_id === selectedFileId)) {
      setSelectedFileId(docs[0].file_id);
    }
  }, [docs, selectedFileId]);

  const selected = docs.find((d) => d.file_id === selectedFileId) ?? null;
  const isEditing = !!selected && editingFileId === selected.file_id;
  const isSaving = !!selected && savingFileId === selected.file_id;

  const baseComponents = useMemo(
    () =>
      buildBaseMarkdownComponents({
        markers: dictWorkspace?.markers ?? {},
        findings: dictWorkspace?.findings ?? {},
        onlineSupport: dictWorkspace?.online_support ?? {},
      }),
    [dictWorkspace?.markers, dictWorkspace?.findings, dictWorkspace?.online_support],
  );

  const refresh = async () => {
    if (workspaceId && userId) {
      await loadSpecificWorkspace(workspaceId, userId);
    }
  };

  const handleApprove = async (fileId: string) => {
    if (!workspaceId) return;
    setError(null);
    setBusyFileId(fileId);
    try {
      const res = await approveDocFile(workspaceId, fileId, undefined, authToken);
      if (res.success) {
        await refresh();
      } else {
        setError(res.error || 'Failed to approve');
      }
    } finally {
      setBusyFileId(null);
    }
  };

  const handleStartEdit = (content: string) => {
    if (!selected) return;
    setSaveError(null);
    editBaselineRef.current = content;
    hasUnsavedRef.current = false;
    setEditingFileId(selected.file_id);
  };

  const handleEditorChange = (markdown: string) => {
    hasUnsavedRef.current = markdown !== editBaselineRef.current;
    if (saveError) setSaveError(null);
  };

  const discardEdits = () => {
    setEditingFileId(null);
    setSaveError(null);
    hasUnsavedRef.current = false;
    editBaselineRef.current = '';
    setDiscardDialogOpen(false);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedRef.current) {
      setDiscardDialogOpen(true);
      return;
    }
    discardEdits();
  };

  const handleSave = async (fileId: string) => {
    if (!workspaceId || savingFileId) return;
    let markdown: string;
    try {
      markdown = editorRef.current?.getMarkdown() ?? editBaselineRef.current;
    } catch {
      setSaveError('Could not read the editor content. Reload the page and try again.');
      return;
    }
    setSavingFileId(fileId);
    setSaveError(null);
    const res = await updateDocFileContent(workspaceId, fileId, markdown, authToken);
    setSavingFileId(null);
    if (!res.success) {
      setSaveError(res.error || 'Failed to save document');
      return;
    }
    setEditingFileId(null);
    hasUnsavedRef.current = false;
    editBaselineRef.current = '';
    await refresh();
  };

  if (docs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-bg-warm p-6">
        <div className="text-center max-w-md">
          <FileText className="w-10 h-10 mx-auto mb-3 text-fg-tertiary" strokeWidth={1.5} />
          <h3 className="text-base font-medium text-fg-secondary mb-1">No generated documents yet</h3>
          <p className="text-sm text-fg-tertiary">
            Model documentation is generated from the model when you accept the
            build. Accept the model to produce it here for review.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex overflow-hidden bg-bg-warm">
      {/* Left: the generated-doc list. */}
      <div className="w-72 flex-shrink-0 border-r border-border-default overflow-y-auto bg-surface">
        <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-fg-tertiary border-b border-border-default">
          Generated documents
        </div>
        <ul>
          {docs.map((f) => {
            const isSel = f.file_id === selectedFileId;
            return (
              <li key={f.file_id}>
                <button
                  type="button"
                  onClick={() => setSelectedFileId(f.file_id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-border-default transition-colors ${
                    isSel ? 'bg-primary-tint' : 'hover:bg-bg-warm'
                  }`}
                >
                  <div className="text-sm font-medium text-fg-secondary truncate">
                    {f.title || f.file_id}
                  </div>
                  <div className="text-xs text-fg-tertiary truncate">
                    {formatProvenance(f)}
                  </div>
                  <div className="mt-1">
                    {f.approved_by ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-success">
                        <CheckCircle2 className="w-3 h-3" /> Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-warning">
                        <Clock className="w-3 h-3" /> Needs review
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right: reader / editor for the selected doc. */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        {selected && (
          <div className="max-w-[820px] mx-auto px-8 py-6">
            {error && (
              <div className="mb-4 flex items-start gap-2 p-3 bg-surface rounded border border-danger/40 text-sm text-danger">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Header: title + provenance + actions. */}
            <div className="flex justify-between items-start gap-3 mb-6 pb-3 border-b border-border-default">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-fg truncate">{selected.title || selected.file_id}</h1>
                <div className="text-xs text-fg-tertiary mt-1">
                  {formatProvenance(selected)}
                  {selected.approved_by && (
                    <>
                      {' · approved by '}
                      {selected.approved_by}
                      {selected.approver_role ? ` (${selected.approver_role})` : ''}
                      {formatApprovedAt(selected.approved_at)}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isEditing ? (
                  <>
                    {saveError && (
                      <span className="text-xs text-danger mr-1" role="alert">{saveError}</span>
                    )}
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong transition-colors disabled:opacity-60"
                    >
                      <X className="w-3.5 h-3.5" /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSave(selected.file_id)}
                      disabled={isSaving}
                      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold transition-colors ${
                        isSaving
                          ? 'bg-surface text-fg-tertiary border border-border-default cursor-wait'
                          : 'bg-primary text-on-primary border border-primary hover:bg-primary-hover'
                      }`}
                    >
                      {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleStartEdit(selected.content || '')}
                      disabled={!workspaceId}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong transition-colors disabled:opacity-50"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    {selected.approved_by ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                        <CheckCircle2 className="w-4 h-4" /> Approved
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleApprove(selected.file_id)}
                        disabled={busyFileId === selected.file_id || !workspaceId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface text-fg-secondary text-sm font-medium rounded-md border border-border-default hover:bg-primary-tint disabled:opacity-50 transition-colors"
                      >
                        {busyFileId === selected.file_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Clock className="w-3.5 h-3.5" />
                        )}
                        Approve
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Body: shared report-grade markdown surface, or the editor. */}
            <div className="report-content max-w-none">
              {isEditing ? (
                <ReportEditor
                  ref={editorRef}
                  initialContent={selected.content || ''}
                  onChange={handleEditorChange}
                />
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={baseComponents}
                >
                  {preprocessDocumentMarkdown(selected.content || '')}
                </ReactMarkdown>
              )}
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={discardDialogOpen}
        onCancel={() => setDiscardDialogOpen(false)}
        onConfirm={discardEdits}
        title="Discard unsaved edits?"
        description="Your changes to this generated document will be lost."
        confirmLabel="Discard"
        variant="destructive"
      />
    </div>
  );
}
